// AntivirusProfiles.tsx
import { useState, useEffect, useCallback } from "react";
import {
    Loader2, Shield, ChevronDown,

} from "lucide-react";
import { showError } from "../../../../../utils/toast";
import baseapi from "../../../../../services/baseapi";


// ── Types ─────────────────────────────────────────────────────────────────────
interface ProtocolConfig {
    "av-scan"?: string;
    "emulator"?: string;
    "fortindr"?: string;
    "quarantine"?: string;
    "fortisandbox"?: string;
    "external-blocklist"?: string;
    "outbreak-prevention"?: string;
    "content-disarm"?: string;
    "executables"?: string;
    [key: string]: any;
}

interface AVProfile {
    name: string;
    comment: string;
    "scan-mode": string;
    "feature-set": string;
    "av-virus-log": string;
    "extended-log": string;
    "mobile-malware-db": string;
    "ems-threat-feed": string;
    "fortisandbox-mode": string;
    "nac-quar": { log: string; expiry: string; infected: string };
    http?: ProtocolConfig;
    ftp?: ProtocolConfig;
    smtp?: ProtocolConfig;
    pop3?: ProtocolConfig;
    imap?: ProtocolConfig;
    mapi?: ProtocolConfig;
    ssh?: ProtocolConfig;
    cifs?: ProtocolConfig;
    nntp?: ProtocolConfig;
    [key: string]: any;
}

// ── Protocol list to show as rows ─────────────────────────────────────────────
const PROTOCOLS = ["http", "ftp", "smtp", "pop3", "imap", "mapi", "ssh", "cifs", "nntp"];

// Protocol color
// const PROTO_COLOR: Record<string, string> = {
//     http: "text-blue-400", ftp: "text-orange-400", smtp: "text-purple-400",
//     pop3: "text-cyan-400", imap: "text-green-400", mapi: "text-yellow-400",
//     ssh: "text-rose-400", cifs: "text-indigo-400", nntp: "text-gray-200",
// };

// ── Value Chip ────────────────────────────────────────────────────────────────
const ValChip = ({ val }: { val: string | undefined }) => {
    if (!val || val === "") return <span className="text-gray-200 text-[9px]">—</span>;

    const style =
        val === "enable" || val === "block"
            ? " bg-green-500/10 border border-green-500/20 text-green-400"
            : val === "disable" || val === "none"
                ? " bg-gray-500/10 border border-gray-500/20 text-gray-200"
                : val === "monitor"
                    ? " bg-blue-500/10 border border-blue-500/20 text-blue-400"
                    : val === "quarantine"
                        ? " bg-yellow-500/10 border border-yellow-500/20 text-yellow-400"
                        : "text-gray-200";

    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-bold uppercase whitespace-nowrap ${style}`}>
            {val}
        </span>
    );
};

// ── Collapsible Profile Table ─────────────────────────────────────────────────
const PROTOCOL_FIELDS: { key: keyof ProtocolConfig; label: string }[] = [
    { key: "av-scan", label: "AV Scan" },
    { key: "outbreak-prevention", label: "Outbreak Prev." },
    { key: "emulator", label: "Emulator" },
    { key: "fortindr", label: "FortiNDR" },
    { key: "fortisandbox", label: "FortiSandbox" },
    { key: "quarantine", label: "Quarantine" },
    { key: "external-blocklist", label: "Ext. Blocklist" },
    { key: "content-disarm", label: "Content Disarm" },
    { key: "executables", label: "Executables" },
];

const ProfileTable = ({ profile, index }: { profile: AVProfile; index: number }) => {
    const [open, setOpen] = useState(true);

    const PROFILE_COLORS = [
        { accent: "text-blue-400", border: "border-blue-500/30", bg: "bg-blue-500/5", icon: "bg-blue-500/10 border-blue-500/20" },
        { accent: "text-purple-400", border: "border-purple-500/30", bg: "bg-purple-500/5", icon: "bg-purple-500/10 border-purple-500/20" },
        { accent: "text-green-400", border: "border-green-500/30", bg: "bg-green-500/5", icon: "bg-green-500/10 border-green-500/20" },
        { accent: "text-orange-400", border: "border-orange-500/30", bg: "bg-orange-500/5", icon: "bg-orange-500/10 border-orange-500/20" },
        { accent: "text-cyan-400", border: "border-cyan-500/30", bg: "bg-cyan-500/5", icon: "bg-cyan-500/10 border-cyan-500/20" },
    ];
    const color = PROFILE_COLORS[index % PROFILE_COLORS.length];

    // Meta badges
    const metaItems = [
        { label: "Scan Mode", val: profile["scan-mode"] },
        { label: "Feature Set", val: profile["feature-set"] },
        { label: "Virus Log", val: profile["av-virus-log"] },
        { label: "Mobile Malware", val: profile["mobile-malware-db"] },
        { label: "EMS Threat Feed", val: profile["ems-threat-feed"] },
        { label: "Extended Log", val: profile["extended-log"] },
        { label: "Sandbox Mode", val: profile["fortisandbox-mode"] },
    ];

    const activeProtocols = PROTOCOLS.filter(p => profile[p]);

    return (
        <div className={`border rounded-xl overflow-hidden ${color.border}`}>
            {/* Header */}
            <button
                onClick={() => setOpen(o => !o)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all ${open ? color.bg : "bg-gray-800/50 hover:bg-gray-800"}`}
            >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border ${color.icon}`}>
                    <Shield size={14} className={color.accent} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className={`text-[13px] font-black ${color.accent}`}>{profile.name}</p>
                    {profile.comment && (
                        <p className="text-[10px] text-gray-200 truncate mt-0.5">{profile.comment}</p>
                    )}
                </div>
                {/* Quick badges */}
                <div className="hidden sm:flex items-center gap-1.5 flex-wrap">
                    <div className="flex flex-col gap-1">
                        <span className="text-[9px] font-bold text-gray-200 uppercase tracking-widest">
                            NAC Quarantine
                        </span>
                        <span className="text-[10px] font-mono text-gray-300">
                            infected={profile["nac-quar"]?.infected} · expiry={profile["nac-quar"]?.expiry}
                        </span>
                    </div>
                </div>
                <div className={`transition-transform duration-200 shrink-0 ml-2 ${open ? "rotate-180" : ""}`}>
                    <ChevronDown size={14} className="text-gray-200" />
                </div>
            </button>

            {open && (
                <div className="border-t border-gray-700/50">

                    {/* Meta Info Row */}
                    <div className="px-2 py-2 bg-gray-800 border border-gray-700/40">
                        <div className="flex items-center gap-4 flex-wrap">
                            {/* Meta Info Grid — label above, value below */}
                            <div className="px-1 py-1">
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-x-6 gap-y-4">
                                    {metaItems.map(({ label, val }) => (
                                        <div key={label} className="flex flex-col gap-1">
                                            <span className="text-[9px] font-bold text-gray-200 uppercase tracking-widest">
                                                {label}
                                            </span>
                                            <ValChip val={val} />
                                        </div>
                                    ))}


                                </div>
                            </div>


                        </div>
                    </div>

                    {/* Protocol Matrix Table */}
                    <div className="overflow-x-auto custom-scroll">
                        <table className="w-full text-left text-xs border-collapse bg-gray-800">
                            <thead className="bg-gray-900/70">
                                <tr className="border-b border-gray-900/50">
                                    <th className="px-4 py-2 text-[10px] font-bold text-gray-200 uppercase tracking-widest sticky left-0  whitespace-nowrap">
                                        Protocol
                                    </th>
                                    {PROTOCOL_FIELDS.map(f => (
                                        <th key={f.key} className="px-3 py-2 text-[10px] font-bold text-gray-200 uppercase tracking-widest whitespace-nowrap">
                                            {f.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700/30">
                                {activeProtocols.map(proto => {
                                    const cfg: ProtocolConfig = profile[proto] ?? {};
                                    return (
                                        <tr key={proto} className="hover:bg-gray-700/20 transition-colors">
                                            {/* Protocol name */}
                                            <td className="px-4 py-2.5 sticky left-0 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-[11px] font-black uppercase text-gray-200`}>
                                                        {proto}
                                                    </span>
                                                </div>
                                            </td>
                                            {PROTOCOL_FIELDS.map(f => (
                                                <td key={f.key} className="px-3 py-2.5 whitespace-nowrap">
                                                    <ValChip val={cfg[f.key]} />
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function AntivirusProfiles({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [profiles, setProfiles] = useState<AVProfile[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await baseapi.get("/firewall_sections", {
                params: {
                    query: JSON.stringify({
                        ...(asset_id && client_id ? { asset_id, client_id } : {}),
                        section_name: "antivirus_profiles",
                    }),
                    limit: 1,
                }
            });
            if (res.data.status && res.data.data?.[0]) {
                const raw = res.data.data[0].results;
                const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                setProfiles(parsed.items ?? []);
            } else showError("Failed to load antivirus profiles");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id]);

    useEffect(() => { fetchData(); }, [fetchData]);

    return (
        <div className="flex flex-col gap-4">
            {/* Content */}
            {loading ? (
                <div className="flex items-center justify-center gap-2 py-20">
                    <Loader2 className="w-4 h-4 animate-spin text-green-400" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">
                        Loading antivirus profiles...
                    </span>
                </div>
            ) : profiles.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-20 opacity-40">
                    <Shield size={24} className="text-gray-200" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">
                        No profiles found
                    </span>
                </div>
            ) : (
                <div className="space-y-4">
                    {profiles.map((profile, i) => (
                        <ProfileTable key={profile.name} profile={profile} index={i} />
                    ))}
                </div>
            )}
        </div>
    );
}
